import { ContactFormPayload, QuoteRequestPayload } from '../types';
import { ContactInfo } from '../types';

const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? 'https://api.abcconstructions.fr/api';

export interface PublicProjectDto {
  id: string;
  title: string;
  summary: string;
  location: string;
  completionDate?: string;
}

export interface PublicJobDto {
  id: string;
  title: string;
  department: string;
  location: string;
  employmentType: string;
  summary: string;
}

export interface HomePageServiceCard {
  title: string;
  description: string;
}

export interface HomePageTestimonial {
  quote: string;
  author: string;
}

export interface HomePageStat {
  label: string;
  value: string;
}

export interface HomePageCarouselSlide {
  src: string;
  alt: string;
  title: string;
  description: string;
}

export interface HomePageContent {
  heroEyebrow: string;
  heroTitle: string;
  heroCopy: string;
  primaryCtaText: string;
  primaryCtaLink: string;
  secondaryCtaText: string;
  secondaryCtaLink: string;
  heroImage: string;
  services: HomePageServiceCard[];
  testimonials: HomePageTestimonial[];
  partners: string[];
  stats: HomePageStat[];
  carouselSlides: HomePageCarouselSlide[];
  showHero?: boolean;
  showServices?: boolean;
  showCarousel?: boolean;
  showTestimonials?: boolean;
  showStats?: boolean;
  showPartners?: boolean;
  showAboutPage?: boolean;
  showPortfolioPage?: boolean;
  showServicesPage?: boolean;
  showCareersPage?: boolean;
  showNewsPage?: boolean;
  showContactPage?: boolean;
  showFaqPage?: boolean;
  showQuotePage?: boolean;
  showLegalPage?: boolean;
}

export const fetchProjects = async (): Promise<PublicProjectDto[]> => {
  const response = await fetch(`${apiBase}/public/projects`);
  if (!response.ok) throw new Error('Unable to load projects.');
  return response.json();
};

export const fetchProject = async (id: string): Promise<PublicProjectDto> => {
  const response = await fetch(`${apiBase}/public/projects/${id}`);
  if (!response.ok) throw new Error('Unable to load project details.');
  return response.json();
};

export const fetchJobs = async (): Promise<PublicJobDto[]> => {
  const response = await fetch(`${apiBase}/public/jobs`);
  if (!response.ok) throw new Error('Unable to load jobs.');
  return response.json();
};

export const fetchJob = async (id: string): Promise<PublicJobDto> => {
  const response = await fetch(`${apiBase}/public/jobs/${id}`);
  if (!response.ok) throw new Error('Unable to load job details.');
  return response.json();
};

export const submitContactForm = async (payload: ContactFormPayload) => {
  const response = await fetch(`${apiBase}/public/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error('Unable to submit contact form.');
  return response.json();
};

export const submitQuoteRequest = async (payload: QuoteRequestPayload & { attachments: File[] }) => {
  const formData = new FormData();
  formData.append('projectType', payload.projectType);
  formData.append('projectDetails', payload.projectDetails);
  formData.append('budgetRange', payload.budgetRange);
  formData.append('timeline', payload.timeline);
  formData.append('companyName', payload.companyName);
  formData.append('contactName', payload.contactName);
  formData.append('email', payload.email);
  formData.append('phone', payload.phone);
  formData.append('location', payload.location);
  formData.append('additionalDetails', payload.additionalDetails);
  formData.append('website', payload.website);

  payload.attachments.forEach((file) => formData.append('attachments', file));

  const response = await fetch(`${apiBase}/public/quote`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) throw new Error('Unable to submit quote request.');
  return response.json();
};

export const fetchHomePageContent = async (): Promise<HomePageContent> => {
  const response = await fetch(`${apiBase}/public/homepage`);
  if (!response.ok) throw new Error('Unable to load homepage content.');
  return response.json();
};

export const fetchContactInfo = async (): Promise<ContactInfo> => {
  const response = await fetch(`${apiBase}/public/contactinfo`);
  if (!response.ok) throw new Error('Unable to load contact info.');
  return response.json();
};
